import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import assignmentRolesService, { type AssignmentRoleDTO } from '../services/assignmentRoles.service';
import { createDedupedCache, type DedupedCache } from '../utils/dedupedCache';
import { useAppFeatures } from './AppFeaturesContext';

/**
 * Registry AssignmentRole'ов организации.
 * Закрывает gap role resolution из /plan-eng-review:
 * role_id → role_code/name для rendering chip'ов в TransitionButtons.
 *
 * TTL 5 минут (роли меняются редко, но не статичны — админ может добавить).
 */

const TTL_MS = 5 * 60_000;

interface AssignmentRolesContextValue {
  /** Все роли (ensured loaded) или пустой массив. */
  roles: readonly AssignmentRoleDTO[];
  /** `true`, если сейчас идёт fetch (первый load). */
  loading: boolean;
  /** Последняя ошибка fetch'а. */
  error: Error | null;
  /** Принудительный refetch. */
  reload: () => Promise<void>;
  /** Lookup role по id — возвращает undefined если нет. */
  byId: (roleId: number) => AssignmentRoleDTO | undefined;
  /** Lookup code по id — для chip'ов. */
  codeFor: (roleId: number) => string | undefined;
  /** Lookup name по id. */
  nameFor: (roleId: number) => string | undefined;
}

const AssignmentRolesContext = createContext<AssignmentRolesContextValue | null>(null);

const CACHE_KEY = 'all-roles';

export function AssignmentRolesProvider({ children }: { children: React.ReactNode }) {
  const appFeatures = useAppFeatures();
  const taskFeatureEnabled = appFeatures.task === true;
  const cacheRef = useRef<DedupedCache<string, readonly AssignmentRoleDTO[]> | null>(null);
  if (!cacheRef.current) {
    cacheRef.current = createDedupedCache<string, readonly AssignmentRoleDTO[]>({
      fetcher: () => assignmentRolesService.listRoles().then((r) => Object.freeze(r)),
      ttlMs: TTL_MS,
    });
  }

  const [roles, setRoles] = useState<readonly AssignmentRoleDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!taskFeatureEnabled) {
      // Assignment roles нужны только для модуля задач — не дёргаем endpoint без фичи.
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await cacheRef.current!.ensureLoaded(CACHE_KEY);
      setRoles(data);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [taskFeatureEnabled]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await cacheRef.current!.refetch(CACHE_KEY);
      setRoles(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rolesMap = useMemo(() => {
    const m = new Map<number, AssignmentRoleDTO>();
    for (const r of roles) m.set(r.id, r);
    return m;
  }, [roles]);

  const byId = useCallback((id: number) => rolesMap.get(id), [rolesMap]);
  const codeFor = useCallback((id: number) => rolesMap.get(id)?.code, [rolesMap]);
  const nameFor = useCallback((id: number) => rolesMap.get(id)?.name, [rolesMap]);

  const value = useMemo<AssignmentRolesContextValue>(
    () => ({ roles, loading, error, reload, byId, codeFor, nameFor }),
    [roles, loading, error, reload, byId, codeFor, nameFor],
  );

  return <AssignmentRolesContext.Provider value={value}>{children}</AssignmentRolesContext.Provider>;
}

export function useAssignmentRoles(): AssignmentRolesContextValue {
  const ctx = useContext(AssignmentRolesContext);
  if (!ctx) {
    throw new Error('useAssignmentRoles must be used inside <AssignmentRolesProvider>');
  }
  return ctx;
}
